import { Color, Spacing } from '@persona-id/design-tokens';
import { FC, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { VerificationStatusSummary } from 'dashboard/models/metrics-verification';
import { FontWeight } from 'lib/interfaces/ThemeSerialized';

const SummaryNumber = styled.text<{ 'data-test'?: string }>`
  font-weight: ${FontWeight.Regular};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  line-height: 20px;
  letter-spacing: 0.1px;
  color: ${Color.PrimaryBlack};
`;

const VerificationMetricsSummaryContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, auto);
  grid-gap: ${Spacing[2]} ${Spacing[5]};
  margin-right: ${Spacing[5]};
`;

const SummaryValueContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: ${Spacing[2]};
  align-items: flex-start;
`;

const SummaryTitle = styled.text`
  font-weight: ${FontWeight.Regular};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  line-height: 20px;
  letter-spacing: 0.1px;
  color: ${({ theme }) => theme.color.text.secondary};
`;

const Summary: FC<PropsWithChildren<{ label: ReactNode }>> = ({ label, children }) => (
  <div>
    <SummaryTitle>{label}</SummaryTitle>
    <SummaryValueContainer>{children}</SummaryValueContainer>
  </div>
);

interface MetricsSummaryProps {
  data: VerificationStatusSummary;
}

const MetricsSummary: FC<MetricsSummaryProps> = ({ data }) => (
  <VerificationMetricsSummaryContainer>
    <Summary
      label={
        <>
          Created
          <HelpIcon>
            This is the number of Verifications created for this verification type.
          </HelpIcon>
        </>
      }
    >
      <SummaryNumber>{formatNumber(data['created'])}</SummaryNumber>
    </Summary>

    <Summary
      data-test='metrics-summary__processed'
      label={
        <>
          Processed • Processed Rate
          <HelpIcon>
            This is the number of created Verifications that Persona was able to process and
            determine whether a Verification should pass, fail, or require a retry.
          </HelpIcon>
        </>
      }
    >
      <SummaryNumber data-test='metrics-summary__value-processed'>
        {data['created'] === 0
          ? `${formatNumber(data['processed'])} •  N/A`
          : `${formatNumber(data['processed'])} • ${formatNumber(data['processed-rate'] * 100)}%`}
      </SummaryNumber>
    </Summary>

    <Summary
      data-test='metrics-summary__passed'
      label={
        <>
          Passed • Pass Rate
          <HelpIcon>
            This is the number of processed Verifications that passed all required checks run during
            processing.
          </HelpIcon>
        </>
      }
    >
      <SummaryNumber data-test='metrics-summary__value-passed'>
        {data['processed'] === 0
          ? `${formatNumber(data['passed'])} • N/A`
          : `${formatNumber(data['passed'])} • ${formatNumber(data['pass-rate'] * 100)}%`}
      </SummaryNumber>
    </Summary>
    <span />
    <span />
  </VerificationMetricsSummaryContainer>
);

export default MetricsSummary;
